// ========================================
// Utility Functions and Global Interceptors
// ========================================

// Session transport and expiry handling live in session-fetch.js.

const Utils = {
    /**
     * Generate a unique UUID v4
     */
    generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    },

    /**
     * Format date to locale string
     */
    formatDate(date) {
        if (!(date instanceof Date)) {
            date = new Date(date);
        }
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    },

    /**
     * Format time to HH:MM
     */
    formatTime(time) {
        if (!time) return '';
        return time;
    },

    /**
     * Get day name from day number (0-6)
     */
    getDayName(dayNumber) {
        const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        return days[dayNumber];
    },

    /**
     * Format currency
     */
    formatCurrency(amount) {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    },

    /**
     * Calculate duration in hours between two times
     */
    calculateDuration(startTime, endTime) {
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);

        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;

        return (endMinutes - startMinutes) / 60;
    },

    /**
     * Validate email format
     */
    validateEmail(email) {
        if (!email) return true; // Optional field
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    },

    /**
     * Validate phone format
     */
    validatePhone(phone) {
        if (!phone) return true; // Optional field
        const re = /^[+]?[\d\s-()]+$/;
        return re.test(phone);
    },

    /**
     * Show toast notification
     */
    showToast(message, type = 'info') {
        const container = document.getElementById('toast-container');
        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;

        const icon = type === 'success' ? 'fa-check-circle' :
            type === 'error' ? 'fa-exclamation-circle' :
                'fa-info-circle';

        SafeHTML.set(toast, `
            <i class="fas ${icon}"></i>
            <span>${this.sanitizeHTML(message)}</span>
        `);

        container.appendChild(toast);

        setTimeout(() => {
            toast.style.animation = 'slideInRight 0.3s ease reverse';
            setTimeout(() => toast.remove(), 300);
        }, 3000);
    },

    /**
     * Show confirmation dialog
     */
    showConfirm(message, onConfirm) {
        const overlay = document.getElementById('modal-overlay');
        const title = document.getElementById('modal-title');
        const body = document.getElementById('modal-body');

        title.textContent = 'Confirmar acción';
        SafeHTML.set(body, `
            <p style="margin-bottom: 1.5rem;">${this.sanitizeHTML(message)}</p>
            <div class="flex gap-1" style="justify-content: flex-end;">
                <button class="btn btn-secondary" id="confirm-cancel">Cancelar</button>
                <button class="btn btn-danger" id="confirm-ok">Confirmar</button>
            </div>
        `);

        overlay.classList.add('active');

        document.getElementById('confirm-cancel').addEventListener('click', (e) => {
            e.stopPropagation();
            this.closeModal();
        });

        document.getElementById('confirm-ok').addEventListener('click', (e) => {
            e.stopPropagation();
            this.closeModal();
            if (typeof onConfirm === 'function') {
                try {
                    console.log('Executing confirm callback...');
                    onConfirm();
                } catch (err) {
                    console.error('Error in confirm callback:', err);
                    alert('Error al ejecutar la acción: ' + err.message);
                }
            }
        });
    },

    /**
     * Open modal
     */
    openModal(title, content) {
        const overlay = document.getElementById('modal-overlay');
        const modalTitle = document.getElementById('modal-title');
        const modalBody = document.getElementById('modal-body');
        const modalActions = document.getElementById('modal-actions');

        modalTitle.textContent = title;
        SafeHTML.set(modalBody, content);
        if (modalActions) {
            SafeHTML.set(modalActions, '');
            modalActions.style.display = 'none';
        }
        overlay.classList.add('active');
        document.body.style.overflow = 'hidden';
    },

    /**
     * Close modal
     */
    closeModal() {
        const overlay = document.getElementById('modal-overlay');
        if (overlay) {
            overlay.classList.remove('active');
        }
        document.body.style.overflow = '';
    },

    /**
     * Sanitize HTML to prevent XSS
     */
    sanitizeHTML(str) {
        // For text and quoted HTML attributes only, never JavaScript/CSS/URL validation.
        return String(str ?? '').replace(/[&<>"']/g, char => ({
            '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;'
        }[char]));
    },

    /**
     * Convierte texto libre en un slug de URL (minúsculas, sin acentos, solo
     * a-z0-9 separados por guiones). Usado para que rutas como la ficha de
     * alumno lleven el nombre en la URL en vez de solo el id.
     */
    slugify(text) {
        return (text || '')
            .toString()
            .trim()
            .toLowerCase()
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    },

    /**
     * Ruta amigable a la ficha de un alumno: "alumno/nombre-apellido-<id>" en
     * vez de solo "alumno/<id>" — el id real sigue yendo al final (formato
     * UUID, con guiones internos) para que App.navigate lo pueda extraer de
     * forma fiable sin depender de que el nombre sea único. Ver
     * App.navigate()'s handling de baseView 'alumno' en app.js.
     */
    studentProfileUrl(student) {
        if (!student || !student.id) return 'alumno';
        const slug = this.slugify(`${student.firstName || ''} ${student.lastName || ''}`);
        return slug ? `alumno/${slug}-${student.id}` : `alumno/${student.id}`;
    },

    /**
     * Strip HTML tags from a string
     */
    stripHTML(html) {
        if (!html) return "";
        // First, unescape characters if they were double-escaped or escaped once
        const doc = new DOMParser().parseFromString(html, 'text/html');
        let text = doc.body.textContent || "";
        // Then, remove any remaining HTML tags just in case
        return text.replace(/<[^>]*>?/gm, '');
    },

    /**
     * Debounce function
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    },

    /**
     * Normalize string (remove accents, lowercase)
     */
    normalizeString(str) {
        if (!str) return '';
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    },

    /**
     * Convert string to Title Case
     */
    /** jsPDF (420 KB) is only needed to export: loaded the first time a PDF is made. */
    loadJsPDF() {
        if (window.jspdf) return Promise.resolve(window.jspdf);
        if (!this._jsPDF) {
            this._jsPDF = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = '/vendor/jspdf.js';
                script.onload = () => window.jspdf ? resolve(window.jspdf) : reject(new Error('jsPDF'));
                script.onerror = () => { script.remove(); reject(new Error('jsPDF')); };
                document.head.append(script);
            });
            this._jsPDF.catch(() => { this._jsPDF = null; });
        }
        return this._jsPDF;
    },

    /** Runs a PDF export now if jsPDF is ready, or once it has loaded. Returns true when it ran now. */
    whenPdfReady(run) {
        if (window.jspdf) return true;
        this.loadJsPDF().then(run, () => this.showToast('No se pudo preparar el PDF. Revisa la conexión y vuelve a intentarlo.', 'error'));
        return false;
    },

    toTitleCase(str) {
        if (!str) return '';
        return str.replace(
            /\w\S*/g,
            function (txt) {
                return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
            }
        );
    }
};

// Make Utils globally available
window.Utils = Utils;
